import { intersection, uniq } from 'lodash-es'
import { isWithinInterval } from '@/utils/dates'
import { useAuthStore } from '@/ContextTab/stores/auth/auth'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import { REFERENCES } from '@/lib/references'
import { NEWS_LINK_TYPE_BY_NAME } from '@/lib/referencesByName'
import { ACCESS } from '@/lib/accessCodes'
import { usePublishedLocalFiltersStore } from '@/ContextTab/stores/newsSet/published/localFilters'
import { useGroupsAndUsersStore } from '@/ContextTab/stores/groupsAndUsers'
import { useCodesStore } from '@/ContextTab/stores/codes/codes'
import {
  type TIdFilterState,
  usePublishedFiltersStore,
} from '@/ContextTab/stores/newsSet/published/filters'
import type { TaskPanelNewsSetType } from '@/lib/types'
import type { NewsItem, TCodeId } from '@/ContextTab/stores/types'


// Здесь собраны предикаты из следующих мест (до 2025-03):
// src/lib/setPredicates/news.ts
// src/ContextApp/stores/news/task-panel/predicates.ts
// ContextApp/stores/news/published/set

export type TNewsPredicate = (newsItem: any) => boolean | Promise<boolean>
type NewsEventId = keyof typeof REFERENCES.NewsEvent.Type
type NewsEventName = typeof REFERENCES.NewsEvent.Type[NewsEventId]['name'][]

export function usePredicates() {

  const authStore = useAuthStore()
  const userProfileStore = useUserProfileStore()
  const groupsAndUsersStore = useGroupsAndUsersStore()
  const codesStore = useCodesStore()
  const publishedLocalFiltersStore = usePublishedLocalFiltersStore()
  const publishedFilterStore = usePublishedFiltersStore()


  // Проверяет, находится ли новость в одном из статусов
  async function checkNewsStatus(
    statusNames: typeof REFERENCES.News.Status[keyof typeof REFERENCES.News.Status]['name'][],
    newsItem: NewsItem,
  ) {
    if (newsItem?.version) {
      const statusId = newsItem.version.status_id
      const statusName = REFERENCES.News.Status[statusId]?.name
      return statusNames.includes(statusName)
    }
    return false
  }

  async function checkByHideRepublicationsFilter(newsItem: NewsItem) {
    return publishedLocalFiltersStore.hideRepublications
      ? (
          newsItem.parent_rel_type_id !== NEWS_LINK_TYPE_BY_NAME['REPUBLICATION'].id
          && newsItem.parent_rel_type_id !== NEWS_LINK_TYPE_BY_NAME['REPUBLICATION_UNEDITED'].id
        )
      : true
  }

  // Если у пользователя есть привязка `can_work` или пользователь в группе
  function checkUserCanWork(newsItem: NewsItem, canWorkIds: (string | number)[]) {
    return (
      !!canWorkIds.find((userId) => authStore.userId === userId)
      || userProfileStore.getIsUserInGroups(newsItem.groups ?? [])
    )
  }

  // Проверка того, работал ли пользователь над содержанием новости
  // https://jira.interfax.ru/browse/PUB-3764
  function checkUserIsContributor(newsItem: NewsItem) {
    const userId = authStore.userId
    if (userId) {
      const user = newsItem?.users?.find(({ user }) => user.id === userId)
      return user
        ? user.is_contributor
        : false
    }
    return false
  }

  function checkIsFromPS(newsItem: NewsItem) {
    return newsItem.origin_id === null
  }

  function checkAgainstCodeFilters(
    allCodes: any,
    filters: { id: TCodeId, state: TIdFilterState }[],
    itemCodesTracks: TCodeId[][],
  ): boolean {

    type AccFilterState = {
      isIncluded: boolean
      isExcluded: boolean
      isIgnored: boolean
      ignoredState: TIdFilterState | null
    }

    const filterHasAtLeastOneRootCodeIncluded = !!filters.filter((code) => {
      return (
        code.state === 'included'
        && allCodes[code.id].track
          .slice(1)
          .reverse()
          .reduce((accumulator, current) => {
            if (accumulator === false) {
              return false
            }
            return !filters.find(({ id }) => id === current)
          }, true)
      )
    }).length

    const itemTracksState = itemCodesTracks.reduce((accumulator, current) => {
      const currentTrackState = current.reduce(
        (accumulator, current) => {
          const presentInFilters = filters.find(({ id }) => id === current)

          if (presentInFilters) {
            if (presentInFilters.state === 'included') {
              if (accumulator.isIgnored) {
                accumulator.ignoredState = 'included'
              }
              accumulator.isIncluded = !accumulator.isExcluded && !accumulator.isIgnored
            } else if (presentInFilters.state === 'excluded') {
              if (accumulator.isIgnored) {
                accumulator.ignoredState = 'excluded'
              }
              accumulator.isExcluded = !accumulator.isIncluded && !accumulator.isIgnored
            } else if (presentInFilters.state === 'ignored') {
              accumulator.isIgnored = !accumulator.isIncluded && !accumulator.isExcluded
            }
          }

          return accumulator
        },
        {
          isIncluded: false,
          isExcluded: false,
          isIgnored: false,
          ignoredState: null,
        } as AccFilterState,
      )

      accumulator.push(currentTrackState)

      return accumulator
    }, [] as AccFilterState[])

    const itemIsIncluded = !!itemTracksState
      .map((state) => state.isIncluded)
      .filter((state) => !!state).length

    const itemIsExcluded = !!itemTracksState
      .map((state) => state.isExcluded)
      .filter((state) => !!state).length

    const itemIsIgnored = !!itemTracksState
      .map((state) => state.isIgnored)
      .filter((state) => !!state).length

    if (itemIsExcluded) {
      return false
    }

    if (itemIsIncluded) {
      return true
    }

    if (itemIsIgnored) {
      const included: boolean[] = itemTracksState
        .filter((state) => state.isIgnored)
        .reduce((accumulator, current) => {
          const include = current.ignoredState === 'included'
            ? false
            : current.ignoredState === 'excluded'
              ? true
              : null
          accumulator.push(include)
          return accumulator
        }, [] as (boolean | null)[])
        .filter((value) => value !== null)

      const isDifferent = uniq(included).length === 2

      if (isDifferent) {
        return filterHasAtLeastOneRootCodeIncluded
      } else {
        return included[0]
      }
    }

    return !filterHasAtLeastOneRootCodeIncluded
  }

  async function checkByCodesFilter(newsItem: NewsItem) {
    const allCodes = codesStore.allCodes

    if (!newsItem.version || !allCodes) {
      return false
    }

    if (!publishedFilterStore.codeFiltersForPredicates.length) {
      return true
    }

    const itemCodesTracks = newsItem.version.codes
      .map((code) => allCodes[code.code.id])
      .filter((code) => !!code)
      .map((code) => [
        code.id,
        ...code.track.slice(1).reverse(),
      ])

    return checkAgainstCodeFilters(
      allCodes,
      publishedFilterStore.codeFiltersForPredicates,
      itemCodesTracks,
    )
  }

  async function checkByFeedFilter(newsItem: NewsItem) {
    const allCodes = codesStore.allCodes

    if (!newsItem.version || !allCodes) {
      return false
    }

    if (!publishedFilterStore.feedCodeFiltersForPredicates.length) {
      return true
    }

    // особая фильтрация для кодов новостей,
    // являющихся переопубликованием без редактирования (PUB-3470)
    const itemCodes = (
      newsItem.parent_rel_type_id === NEWS_LINK_TYPE_BY_NAME['REPUBLICATION_UNEDITED'].id
    )
      ? newsItem.version.codes.filter((code) => code.is_added_by_republication_unedited)
      : newsItem.version.codes

    const mappedItemCodes = itemCodes
      .map((code) => allCodes[code.code.id])
      .filter((code) => !!code)

    const itemCodesTracks = mappedItemCodes.map((code) => [
      code.id,
      ...code.track.slice(1).reverse(),
    ])

    return checkAgainstCodeFilters(
      allCodes,
      publishedFilterStore.feedCodeFiltersForPredicates,
      itemCodesTracks,
    )
  }

  async function checkByUsersFilter(newsItem: NewsItem) {
    if (!publishedFilterStore.userFiltersForPredicates.length) {
      return true
    }

    const newsItemVersion = newsItem.version
    if (!newsItem || !newsItemVersion) {
      return false
    }

    type FilterState = {
      ids: string[]
      excludedIds: string[]
      ignoredIds: string[]
    }
    const initialState: FilterState = { ids: [], excludedIds: [], ignoredIds: [] }

    const addItemsToAccumulatorByState = (
      accumulator: any,
      name: string,
      id: string,
      state: any,
    ) => {
      if (/G$/g.test(id)) {
        const groupChildren = groupsAndUsersStore.groupAndUsersById[id]?.children
        if (!groupChildren) {
          return
        }
        groupChildren.forEach((child: any) => {
          const isInValues = publishedFilterStore.userFiltersForPredicates.find(
            (item: any) => item.id === child,
          )

          if (isInValues && isInValues.state !== state) {
            return
          }
          accumulator[name].push(child)
        })
      } else {
        accumulator[name].push(id)
      }
    }

    const processedValues: FilterState = publishedFilterStore.userFiltersForPredicates.reduce(
      (accumulator: FilterState, current: any) => {
        if (current.state === 'included') {
          addItemsToAccumulatorByState(accumulator, 'ids', current.id, 'included')
        } else if (current.state === 'excluded') {
          addItemsToAccumulatorByState(
            accumulator,
            'excludedIds',
            current.id,
            'excluded',
          )
        } else if (current.state === 'ignored') {
          addItemsToAccumulatorByState(
            accumulator,
            'ignoredIds',
            current.id,
            'ignored',
          )
        }

        return accumulator
      },
      initialState,
    )

    const authors = newsItemVersion.authors.map((author: any) => author.user.id)
    const publisher = newsItem.publisher?.id
    const users = [...authors, ...(publisher
      ? [publisher]
      : [])]
    const hasExcluded = intersection(processedValues.excludedIds, users).length

    if (hasExcluded) {
      return false
    } else if (
      processedValues.ids.length
      && !intersection(processedValues.ids, users).length
    ) {
      return false
    }
    return true
  }

  function checkByDatesFilter(newsItem: NewsItem) {
    if (
      !publishedLocalFiltersStore.dates?.start
      || !publishedLocalFiltersStore.dates?.end
    ) {
      return true
    }
    return isWithinInterval(
      new Date(newsItem.status_modified_at),
      {
        start: publishedLocalFiltersStore.dates?.start ?? new Date(),
        end: publishedLocalFiltersStore.dates?.end ?? new Date(),
      },
    )
  }

  function checkIsInUserGroups(newsItem: NewsItem) {
    const filterGroups = userProfileStore.profile.filters?.news_waiting?.groups?.ids ?? []
    const filterUsers = userProfileStore.profile.filters?.news_waiting?.users?.ids ?? []

    const canWorkIds = (newsItem?.users ?? [])
      .filter((user) => user.can_work)
      .map((user) => user.user.id)

    if (filterGroups?.length || filterUsers?.length) {
      let isInGroups = false
      let isToMe = false

      if (filterGroups?.length) {
        isInGroups = newsItem.groups?.length > 0
          && newsItem.groups
            .map((link) => link.group.id)
            .reduce((acc, id) => {
              if (acc === true) {
                return acc
              }
              acc = filterGroups.includes(id)
              return acc
            }, false)
      }

      if (filterUsers?.length) {
        isToMe = Boolean(
          filterUsers.find((id: number) => canWorkIds.includes(id)),
        )
      }

      return isInGroups || isToMe
    } else {
      return checkUserCanWork(newsItem, canWorkIds)
    }
  }

  function checkIsMine(newsItem: NewsItem) {
    return (
      authStore.userId
      && newsItem.version?.user?.id === authStore.userId
    )
  }

  function checkIsUserNewsOrUserHasRightToSeeIt(newsItem: NewsItem) {
    const isUserNews = checkIsMine(newsItem)

    const permissions = authStore.tokenData?.permissions ?? []
    const userHasPermission = permissions.includes(ACCESS.NEWS.GROUPMATES_NEWS)

    const newsCurrentUserGroups = newsItem.version?.user?.group_ids || []
    const isUserInGroups = userProfileStore.getIsUserInGroups(
      newsCurrentUserGroups.map((id: number) => ({ group: { id } })),
    )
    return isUserNews || (userHasPermission && isUserInGroups)
  }

  // function, not related with newsItem, but used for same purposes
  function checkByNewsEvent(excludeEvents: NewsEventName, typeId: NewsEventId) {
    const eventName = REFERENCES.NewsEvent.Type[typeId]?.name
    return !excludeEvents.includes(eventName)
  }

  const publishedPredicates = [
    checkNewsStatus.bind(null, ['PUBLISHED']),
    checkByHideRepublicationsFilter.bind(null),
    checkByDatesFilter.bind(null),
    checkByUsersFilter.bind(null),
    checkByCodesFilter.bind(null),
    checkByFeedFilter.bind(null),
    // checkByNewsEvent also should be checked, but doesn't depend on newsItem: extracted
  ]

  const taskPanelPredicates: Record<TaskPanelNewsSetType, TNewsPredicate[]> = {
    waiting: [
      checkNewsStatus.bind(null, ['WAITS_FOR_EDIT']),
      checkIsInUserGroups.bind(null),
    ],
    inProgress: [
      checkNewsStatus.bind(null, ['DRAFT', 'ON_EDIT']),
      checkIsUserNewsOrUserHasRightToSeeIt.bind(null),
    ],
    my: [
      checkUserIsContributor.bind(null),
      checkIsFromPS.bind(null),
    ],
  }

  async function checkAgainstPredicates(
    predicates: TNewsPredicate[],
    newsItem: NewsItem,
  ): Promise<boolean> {
    for await (const predicate of predicates) {
      if (!(await predicate(newsItem))) {
        return false
      }
    }
    return true
  }

  return {
    publishedPredicates,
    taskPanelPredicates,
    checkAgainstPredicates,
    checkByNewsEvent,
    checkIsMine,
  }
}
